

<template>
  <div class="dashboard-bg py-10">
    <v-container>
      
      <div class="dashboard-hero">
        <div class="dashboard-hero__heading">
          <img src="@/assets/logoSemFundo.png" alt="Logo" width="64" height="50" class="dashboard-hero__logo" />
          <div>
            <h1 class="dashboard-title">Painel da Transportadora</h1>
            <div class="dashboard-subtitle">
              Financeiro, viagens, frota e compliance com dados reais do sistema.
            </div>
          </div>
        </div>

        <div class="dashboard-hero__company">
          <div class="dashboard-hero__eyebrow">Visao institucional</div>
          <div class="dashboard-hero__name">{{ transportadora.nome || 'Transportadora' }}</div>
          <div class="dashboard-hero__chips">
            <v-chip small label class="dashboard-chip dashboard-chip--primary">
              Plano {{ assinatura.plano_nome }}
            </v-chip>
            <v-chip
              small
              label
              :class="['dashboard-chip', assinatura.acesso_liberado ? 'dashboard-chip--success' : 'dashboard-chip--warning']"
            >
              {{ assinatura.status }}
            </v-chip>
            <v-chip small label class="dashboard-chip dashboard-chip--ghost">
              {{ periodLabel }}
            </v-chip>
          </div>
          <div class="dashboard-hero__summary">{{ assinatura.resumo_status }}</div>
        </div>
      </div>

      <v-card class="dashboard-filter-card mt-6 mb-6" outlined>
        <div class="dashboard-filter-card__grid">
          <v-select
            v-model="filters.period"
            :items="periodOptions"
            item-text="text"
            item-value="value"
            label="Periodo"
            outlined
            dense
            hide-details
            class="dashboard-filter-card__field"
            @change="loadDashboard"
          />

          <v-select
            v-model="filters.alert_days"
            :items="alertWindowOptions"
            item-text="text"
            item-value="value"
            label="Janela de alerta"
            outlined
            dense
            hide-details
            class="dashboard-filter-card__field"
            @change="loadDashboard"
          />

          <v-btn class="dashboard-refresh-btn" depressed :loading="loading" @click="loadDashboard">
            <v-icon left size="18">mdi-refresh</v-icon>
            Atualizar painel
          </v-btn>
        </div>

        <div class="dashboard-filter-card__footer">
          <span>Periodo selecionado afeta financeiro e viagens. Alertas seguem a janela configurada.</span>
          <span>{{ generatedAtLabel }}</span>
          <span v-if="usuario.nome">Responsavel conectado: {{ usuario.nome }} ({{ usuario.tipo }})</span>
        </div>

        <v-progress-linear v-if="loading" indeterminate color="#53b7a6" rounded class="mt-4" />
      </v-card>

      <v-row dense class="mb-2">
        <v-col cols="12" sm="6" lg="3">
          <v-card class="pa-5 dashboard-card dashboard-kpi-card green-gradient">
            <div class="dashboard-kpi__header">
              <v-icon large class="dashboard-kpi__icon">mdi-cash-check</v-icon>
              <span class="ml-3 font-weight-bold">Resultado realizado</span>
            </div>
            <div class="dashboard-kpi__value">{{ formatCurrency(cards.resultado_realizado) }}</div>
            <v-sparkline :value="financeSparkline" color="#8fd2c5" height="28" auto-draw />
            <div class="dashboard-kpi__caption">
              Recebido {{ formatCurrency(resumo.financeiro.receber_realizado) }} e pago
              {{ formatCurrency(resumo.financeiro.pagar_realizado) }} no periodo.
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" lg="3">
          <v-card class="pa-5 dashboard-card dashboard-kpi-card blue-gradient">
            <div class="dashboard-kpi__header">
              <v-icon large class="dashboard-kpi__icon">mdi-calendar-clock-outline</v-icon>
              <span class="ml-3 font-weight-bold">Agenda liquida</span>
            </div>
            <div class="dashboard-kpi__value">{{ formatCurrency(cards.saldo_previsto) }}</div>
            <v-sparkline :value="financeSparkline" color="#9fb9c8" height="28" auto-draw />
            <div class="dashboard-kpi__caption">
              Previsto para receber {{ formatCurrency(resumo.financeiro.receber_previsto) }} e pagar
              {{ formatCurrency(resumo.financeiro.pagar_previsto) }}.
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" lg="3">
          <v-card class="pa-5 dashboard-card dashboard-kpi-card amber-gradient">
            <div class="dashboard-kpi__header">
              <v-icon large class="dashboard-kpi__icon">mdi-map-marker-path</v-icon>
              <span class="ml-3 font-weight-bold">Viagens do periodo</span>
            </div>
            <div class="dashboard-kpi__value">{{ cards.viagens_periodo }}</div>
            <v-sparkline :value="tripSparkline" color="#b9c5ce" height="28" auto-draw />
            <div class="dashboard-kpi__caption">
              {{ resumo.operacao.viagens_ativas_atuais }} em operacao agora,
              {{ resumo.operacao.viagens_finalizadas_periodo }} finalizadas no filtro.
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" lg="3">
          <v-card class="pa-5 dashboard-card dashboard-kpi-card slate-gradient">
            <div class="dashboard-kpi__header">
              <v-icon large class="dashboard-kpi__icon">mdi-alert-octagon-outline</v-icon>
              <span class="ml-3 font-weight-bold">Alertas criticos</span>
            </div>
            <div class="dashboard-kpi__value">{{ cards.alertas_criticos }}</div>
            <div class="dashboard-kpi__chips">
              <v-chip x-small label class="dashboard-chip dashboard-chip--danger">
                {{ resumo.financeiro.atrasados }} financeiros atrasados
              </v-chip>
              <v-chip x-small label class="dashboard-chip dashboard-chip--warning">
                {{ resumo.compliance.vencidos }} documentos vencidos
              </v-chip>
            </div>
            <div class="dashboard-kpi__caption">
              {{ resumo.compliance.proximos }} documentos na janela de alerta e
              {{ resumo.compliance.sem_informacao }} sem validade informada.
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12" lg="8">
          <v-card class="pa-6 dashboard-card dashboard-surface-card mb-4" outlined>
            <div class="dashboard-section__header">
              <div>
                <div class="dashboard-section__eyebrow">Financeiro</div>
                <div class="dashboard-section__title">Fluxo do periodo</div>
                <div class="dashboard-section__subtitle">
                  Entradas e saidas com base na data prevista dos lancamentos.
                </div>
              </div>
              <v-chip small label class="dashboard-chip dashboard-chip--ghost">{{ charts.fluxo_financeiro.length }} pontos</v-chip>
            </div>

            <div class="dashboard-metric-strip">
              <div class="dashboard-metric-strip__item">
                <span>Receber</span>
                <strong>{{ formatCurrency(resumo.financeiro.receber_previsto) }}</strong>
              </div>
              <div class="dashboard-metric-strip__item">
                <span>Pagar</span>
                <strong>{{ formatCurrency(resumo.financeiro.pagar_previsto) }}</strong>
              </div>
              <div class="dashboard-metric-strip__item">
                <span>Pendentes</span>
                <strong>{{ resumo.financeiro.pendentes }}</strong>
              </div>
            </div>

            <v-sparkline
              :value="financeSparkline"
              color="#53b7a6"
              height="80"
              line-width="3"
              auto-draw
              smooth
            />

            <div class="dashboard-bar-chart" v-if="charts.fluxo_financeiro.length">
              <div v-for="item in charts.fluxo_financeiro" :key="item.key" class="dashboard-bar-chart__item">
                <div class="dashboard-bar-chart__columns">
                  <div
                    class="dashboard-bar-chart__bar dashboard-bar-chart__bar--in"
                    :style="{ height: `${getColumnHeight(item.entradas, financeBarMax)}%` }"
                  />
                  <div
                    class="dashboard-bar-chart__bar dashboard-bar-chart__bar--out"
                    :style="{ height: `${getColumnHeight(item.saidas, financeBarMax)}%` }"
                  />
                </div>
                <div class="dashboard-bar-chart__label">{{ item.label }}</div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" lg="4">
          <v-card class="pa-6 dashboard-card dashboard-surface-card mb-4" outlined>
            <div class="dashboard-section__header">
              <div>
                <div class="dashboard-section__eyebrow">Compliance e frota</div>
                <div class="dashboard-section__title">Saude operacional</div>
              </div>
              <v-chip small label class="dashboard-chip dashboard-chip--ghost">{{ compliancePercent }}% em dia</v-chip>
            </div>

            <div class="dashboard-donut-wrap">
              <div class="dashboard-donut" :style="complianceDonutStyle">
                <div class="dashboard-donut__center">
                  <strong>{{ compliancePercent }}%</strong>
                  <span>em dia</span>
                </div>
              </div>

              <div class="dashboard-legend">
                <div class="dashboard-legend__item">
                  <span class="dashboard-legend__dot is-green" />
                  <span>Em dia</span>
                  <strong>{{ resumo.compliance.em_dia }}</strong>
                </div>
                <div class="dashboard-legend__item">
                  <span class="dashboard-legend__dot is-amber" />
                  <span>Proximos</span>
                  <strong>{{ resumo.compliance.proximos }}</strong>
                </div>
                <div class="dashboard-legend__item">
                  <span class="dashboard-legend__dot is-red" />
                  <span>Vencidos</span>
                  <strong>{{ resumo.compliance.vencidos }}</strong>
                </div>
                <div class="dashboard-legend__item">
                  <span class="dashboard-legend__dot is-slate" />
                  <span>Sem validade</span>
                  <strong>{{ resumo.compliance.sem_informacao }}</strong>
                </div>
              </div>
            </div>

            <div class="dashboard-mini-grid">
              <div class="dashboard-mini-grid__item">
                <span>Veiculos ativos</span>
                <strong>{{ resumo.frota.veiculos_ativos }}</strong>
              </div>
              <div class="dashboard-mini-grid__item">
                <span>Motoristas ativos</span>
                <strong>{{ resumo.frota.motoristas_ativos }}</strong>
              </div>
              <div class="dashboard-mini-grid__item">
                <span>Usuarios ativos</span>
                <strong>{{ resumo.frota.usuarios_ativos }}</strong>
              </div>
              <div class="dashboard-mini-grid__item">
                <span>Janela de alerta</span>
                <strong>{{ filters.alert_days }} dias</strong>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12" lg="5">
          <v-card class="pa-6 dashboard-card dashboard-surface-card mb-4" outlined>
            <div class="dashboard-section__header">
              <div>
                <div class="dashboard-section__eyebrow">Operacao</div>
                <div class="dashboard-section__title">Viagens por status</div>
              </div>
              <v-chip small label class="dashboard-chip dashboard-chip--ghost">{{ resumo.operacao.viagens_ativas_atuais }} ativas agora</v-chip>
            </div>

            <div class="dashboard-metric-strip dashboard-metric-strip--three">
              <div class="dashboard-metric-strip__item">
                <span>Frete liquido</span>
                <strong>{{ formatCurrency(resumo.operacao.frete_liquido ?? resumo.operacao.frete_contratado) }}</strong>
              </div>
              <div class="dashboard-metric-strip__item">
                <span>Despesa viagem</span>
                <strong>{{ formatCurrency(resumo.operacao.despesa_viagens) }}</strong>
              </div>
              <div class="dashboard-metric-strip__item">
                <span>Margem</span>
                <strong>{{ formatPercent(resumo.operacao.margem_percentual) }}</strong>
              </div>
            </div>

            <v-sparkline
              :value="tripSparkline"
              color="#7aa2b6"
              height="72"
              line-width="3"
              auto-draw
              smooth
            />

            <div class="dashboard-progress-list" v-if="charts.viagens_por_status.length">
              <div v-for="item in charts.viagens_por_status" :key="item.label" class="dashboard-progress-list__row">
                <span>{{ item.label }}</span>
                <div class="dashboard-progress-list__track">
                  <div class="dashboard-progress-list__fill" :style="{ width: `${getRatioWidth(item.value, statusMax)}%` }" />
                </div>
                <strong>{{ item.value }}</strong>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" lg="7">
          <v-card class="pa-6 dashboard-card dashboard-surface-card mb-4" outlined>
            <div class="dashboard-section__header">
              <div>
                <div class="dashboard-section__eyebrow">Mix financeiro</div>
                <div class="dashboard-section__title">Categorias e clientes em destaque</div>
              </div>
            </div>

            <v-row dense>
              <v-col cols="12" md="7">
                <div class="dashboard-ranking__title">Categorias mais movimentadas</div>
                <div v-if="charts.categorias_financeiras.length" class="dashboard-ranking">
                  <div v-for="item in charts.categorias_financeiras" :key="item.label" class="dashboard-ranking__item">
                    <div class="dashboard-ranking__meta">
                      <strong>{{ item.label }}</strong>
                      <span>{{ formatCurrency(item.receber) }} entrar • {{ formatCurrency(item.pagar) }} sair</span>
                    </div>
                    <div class="dashboard-ranking__track">
                      <div class="dashboard-ranking__fill" :style="{ width: `${getRatioWidth(item.total, categoryMax)}%` }" />
                    </div>
                    <div class="dashboard-ranking__value">{{ formatCurrency(item.total) }}</div>
                  </div>
                </div>
                <div v-else class="dashboard-empty-state">
                  Nenhuma categoria movimentada no periodo selecionado.
                </div>
              </v-col>

              <v-col cols="12" md="5">
                <div class="dashboard-aside__title">Clientes com maior frete liquido</div>
                <div v-if="lists.contratantes_destaque.length" class="dashboard-aside__list">
                  <div v-for="item in lists.contratantes_destaque" :key="item.nome" class="dashboard-aside__item">
                    <div>
                      <strong>{{ item.nome }}</strong>
                      <span>{{ item.viagens }} viagens no filtro</span>
                    </div>
                    <div class="dashboard-aside__value">{{ formatCurrency(item.frete_liquido_total ?? item.frete_total) }}</div>
                  </div>
                </div>
                <div v-else class="dashboard-empty-state">
                  Sem frete consolidado por cliente neste periodo.
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12" lg="6">
          <v-card class="pa-6 dashboard-card dashboard-surface-card mb-4" outlined>
            <div class="dashboard-section__header">
              <div>
                <div class="dashboard-section__eyebrow">Pendencias</div>
                <div class="dashboard-section__title">Alertas e proximas acoes</div>
              </div>
              <v-chip small label class="dashboard-chip dashboard-chip--ghost">{{ lists.alertas.length }} alertas</v-chip>
            </div>

            <div v-if="lists.alertas.length" class="dashboard-alert-list">
              <div v-for="(item, index) in lists.alertas" :key="`${item.tipo}-${index}`" class="dashboard-alert-list__item">
                <div class="dashboard-alert-list__icon" :class="getAlertTone(item)">
                  <v-icon small>{{ getAlertIcon(item) }}</v-icon>
                </div>
                <div class="dashboard-alert-list__content">
                  <div class="dashboard-alert-list__title">{{ item.titulo }}</div>
                  <div class="dashboard-alert-list__description">{{ item.descricao }}</div>
                  <div class="dashboard-alert-list__meta">
                    {{ item.referencia }} • {{ formatDate(item.data) }}
                    <span v-if="item.valor !== null">• {{ formatCurrency(item.valor) }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="dashboard-empty-state">
              Nenhum alerta critico encontrado para a janela selecionada.
            </div>

            <div class="dashboard-subsection">
              <div class="dashboard-subsection__title">Proximos compromissos financeiros</div>
              <div v-if="lists.proximos_lancamentos.length" class="dashboard-subsection__list">
                <div v-for="item in lists.proximos_lancamentos.slice(0, 5)" :key="item.id" class="dashboard-subsection__item">
                  <div>
                    <strong>{{ item.descricao }}</strong>
                    <span>{{ item.tipo }} • {{ item.categoria }} • {{ formatDate(item.data_prevista) }}</span>
                  </div>
                  <div class="dashboard-subsection__value">{{ formatCurrency(item.valor) }}</div>
                </div>
              </div>
              <div v-else class="dashboard-empty-state dashboard-empty-state--compact">
                Nenhum compromisso pendente dentro da janela de alerta.
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" lg="6">
          <v-card class="pa-6 dashboard-card dashboard-surface-card mb-4" outlined>
            <div class="dashboard-section__header">
              <div>
                <div class="dashboard-section__eyebrow">Viagens</div>
                <div class="dashboard-section__title">Viagens recentes e margem</div>
              </div>
              <v-chip small label class="dashboard-chip dashboard-chip--ghost">{{ lists.viagens_recentes.length }} registros</v-chip>
            </div>

            <div v-if="lists.viagens_recentes.length" class="dashboard-trip-list">
              <div v-for="item in lists.viagens_recentes" :key="item.id" class="dashboard-trip-list__item">
                <div>
                  <div class="dashboard-trip-list__title">{{ item.codigo }}</div>
                  <div class="dashboard-trip-list__meta">
                    {{ item.motorista_nome }} • {{ item.veiculo_placa }} • {{ item.contratante_nome }}
                  </div>
                  <div class="dashboard-trip-list__detail">
                    Saida {{ formatDate(item.data_saida) }} • Frete bruto {{ formatCurrency(item.valor_frete) }} •
                    Liquido {{ formatCurrency(item.frete_liquido ?? item.valor_frete) }} •
                    Despesa {{ formatCurrency(item.despesa_total) }}
                  </div>
                </div>

                <div class="dashboard-trip-list__aside">
                  <span class="dashboard-status-pill" :class="getTripStatusClass(item.status)">
                    {{ item.status }}
                  </span>
                  <strong>{{ formatCurrency(item.margem) }}</strong>
                  <span>{{ formatPercent(item.margem_percentual) }}</span>
                </div>
              </div>
            </div>
            <div v-else class="dashboard-empty-state">
              Nenhuma viagem encontrada dentro do periodo selecionado.
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import dashboardService from '../dashboardService';
import { formatCurrencyDisplay } from '../../financeiro/financeiroCurrency';

const PERIOD_OPTIONS = [
  { text: 'Hoje', value: 'today' },
  { text: 'Ultimos 7 dias', value: '7d' },
  { text: 'Ultimos 30 dias', value: '30d' },
  { text: 'Ultimos 90 dias', value: '90d' },
  { text: 'Ano atual', value: 'year' },
  { text: 'Todo o historico', value: 'all' },
];

const ALERT_WINDOW_OPTIONS = [
  { text: '7 dias', value: 7 },
  { text: '15 dias', value: 15 },
  { text: '30 dias', value: 30 },
  { text: '60 dias', value: 60 },
];

const DRIVER_EXPENSE_WHATSAPP_DISPLAY = '62 99333 9138';
const DRIVER_EXPENSE_WHATSAPP_URL = `https://wa.me/5562993339138?text=${encodeURIComponent('Ola! Quero lancar uma despesa de viagem.')}`;

export default {
  name: 'InicioPage',

  data() {
    return {
      loading: false,
      dashboard: null,
      filters: {
        period: '30d',
        alert_days: 30,
      },
      driverExpenseWhatsappDisplay: DRIVER_EXPENSE_WHATSAPP_DISPLAY,
      periodOptions: PERIOD_OPTIONS,
      alertWindowOptions: ALERT_WINDOW_OPTIONS,
    };
  },

  computed: {
    transportadora() {
      return this.dashboard?.transportadora || {};
    },

    usuario() {
      return this.dashboard?.usuario || {};
    },

    assinatura() {
      return this.dashboard?.assinatura || {
        plano_nome: 'Sem assinatura',
        status: 'SEM_ASSINATURA',
        resumo_status: 'Nenhuma assinatura encontrada para a transportadora.',
        acesso_liberado: false,
      };
    },

    cards() {
      return this.dashboard?.cards || {
        resultado_realizado: 0,
        saldo_previsto: 0,
        viagens_periodo: 0,
        alertas_criticos: 0,
      };
    },

    resumo() {
      return this.dashboard?.resumo || {
        financeiro: {
          receber_previsto: 0,
          pagar_previsto: 0,
          saldo_previsto: 0,
          receber_realizado: 0,
          pagar_realizado: 0,
          resultado_realizado: 0,
          pendentes: 0,
          atrasados: 0,
          vencendo_janela: 0,
        },
        operacao: {
          viagens_periodo: 0,
          viagens_ativas_atuais: 0,
          viagens_finalizadas_periodo: 0,
          frete_contratado: 0,
          frete_liquido: 0,
          despesa_viagens: 0,
          margem_total: 0,
          margem_percentual: null,
        },
        frota: {
          veiculos_ativos: 0,
          motoristas_ativos: 0,
          usuarios_ativos: 0,
        },
        compliance: {
          total: 0,
          em_dia: 0,
          proximos: 0,
          vencidos: 0,
          sem_informacao: 0,
        },
      };
    },

    charts() {
      return this.dashboard?.charts || {
        fluxo_financeiro: [],
        categorias_financeiras: [],
        viagens_por_status: [],
        viagens_tendencia: [],
      };
    },

    lists() {
      return this.dashboard?.lists || {
        alertas: [],
        proximos_lancamentos: [],
        viagens_recentes: [],
        contratantes_destaque: [],
      };
    },

    periodLabel() {
      return this.dashboard?.filtros?.period_label || 'Ultimos 30 dias';
    },

    generatedAtLabel() {
      if (!this.dashboard?.generated_at) {
        return 'Sem atualizacao ainda';
      }

      return `Ultima atualizacao: ${this.formatDateTime(this.dashboard.generated_at)}`;
    },

    financeSparkline() {
      const values = this.charts.fluxo_financeiro.map((item) => Number(item.resultado || 0));
      return values.length ? values : [0];
    },

    tripSparkline() {
      const values = this.charts.viagens_tendencia.map((item) => Number(item.quantidade || 0));
      return values.length ? values : [0];
    },

    financeBarMax() {
      const values = this.charts.fluxo_financeiro.map((item) => Math.max(Number(item.entradas || 0), Number(item.saidas || 0)));
      return values.length ? Math.max(...values, 1) : 1;
    },

    statusMax() {
      const values = this.charts.viagens_por_status.map((item) => Number(item.value || 0));
      return values.length ? Math.max(...values, 1) : 1;
    },

    categoryMax() {
      const values = this.charts.categorias_financeiras.map((item) => Number(item.total || 0));
      return values.length ? Math.max(...values, 1) : 1;
    },

    compliancePercent() {
      const total = Number(this.resumo.compliance.total || 0);
      if (!total) {
        return 0;
      }

      return Math.round((Number(this.resumo.compliance.em_dia || 0) / total) * 100);
    },

    complianceDonutStyle() {
      const total = Number(this.resumo.compliance.total || 0);
      if (!total) {
        return {
          background: 'conic-gradient(#334155 0 100%)',
        };
      }

      const emDia = (Number(this.resumo.compliance.em_dia || 0) / total) * 100;
      const proximos = (Number(this.resumo.compliance.proximos || 0) / total) * 100;
      const vencidos = (Number(this.resumo.compliance.vencidos || 0) / total) * 100;
      const semInfo = (Number(this.resumo.compliance.sem_informacao || 0) / total) * 100;
      const p1 = emDia;
      const p2 = emDia + proximos;
      const p3 = p2 + vencidos;
      const p4 = Math.min(100, p3 + semInfo);

      return {
        background: `conic-gradient(#53b7a6 0 ${p1}%, #c79661 ${p1}% ${p2}%, #d37a7a ${p2}% ${p3}%, #8195a4 ${p3}% ${p4}%, #1e293b ${p4}% 100%)`,
      };
    },
  },

  methods: {
    async loadDashboard() {
      this.loading = true;

      try {
        const response = await dashboardService.getOverview(this.filters);
        this.dashboard = response.data || null;
      } catch (error) {
        const errorMessage = error?.response?.data?.error || 'Nao foi possivel carregar o dashboard.';
        this.$toasted?.error(errorMessage);
      } finally {
        this.loading = false;
      }
    },

    formatCurrency(value) {
      return formatCurrencyDisplay(value);
    },

    formatDate(value) {
      if (!value) {
        return '-';
      }

      const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }

      return date.toLocaleDateString('pt-BR');
    },

    formatDateTime(value) {
      if (!value) {
        return '-';
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }

      return date.toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    },

    formatPercent(value) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }

      return `${Number(value).toFixed(1)}%`;
    },

    openDriverExpenseWhatsapp() {
      window.open(DRIVER_EXPENSE_WHATSAPP_URL, '_blank', 'noopener,noreferrer');
    },

    goToMotoristas() {
      if (this.$route?.path !== '/motoristas') {
        this.$router.push('/motoristas');
      }
    },

    getColumnHeight(value, maxValue) {
      if (!value || !maxValue) {
        return 4;
      }

      return Math.max(10, (Number(value) / Number(maxValue)) * 100);
    },

    getRatioWidth(value, maxValue) {
      if (!value || !maxValue) {
        return 8;
      }

      return Math.max(8, (Number(value) / Number(maxValue)) * 100);
    },

    getAlertTone(item) {
      return item.severidade === 'alta' ? 'is-high' : 'is-medium';
    },

    getAlertIcon(item) {
      if (item.tipo === 'documento') {
        return 'mdi-file-alert-outline';
      }

      return item.severidade === 'alta' ? 'mdi-alert-circle-outline' : 'mdi-clock-alert-outline';
    },

    getTripStatusClass(status) {
      if (['Em andamento', 'Carregando', 'Ag. Descarga'].includes(status)) {
        return 'is-active';
      }

      if (status === 'Finalizada') {
        return 'is-done';
      }

      if (status === 'Cancelada') {
        return 'is-cancelled';
      }

      return 'is-pending';
    },
  },

  created() {
    this.loadDashboard();
  },
};
</script>

<style scoped>
.dashboard-bg {
  --dashboard-text: #1f2a44;
  --dashboard-muted: #7b8aa2;
  --dashboard-muted-strong: #526277;
  --dashboard-surface: rgba(255, 255, 255, 0.9);
  --dashboard-surface-strong: #ffffff;
  --dashboard-surface-soft: #f7fafc;
  --dashboard-border: rgba(123, 138, 162, 0.16);
  --dashboard-accent: #12b57a;
  --dashboard-accent-soft: rgba(18, 181, 122, 0.1);
  --dashboard-secondary: #7b8aa2;
  --dashboard-secondary-soft: rgba(123, 138, 162, 0.12);
  --dashboard-warning: #c7924a;
  --dashboard-warning-soft: rgba(199, 146, 74, 0.12);
  --dashboard-danger: #d36b6b;
  --dashboard-danger-soft: rgba(211, 107, 107, 0.12);
  min-height: 100vh;
  background:
    radial-gradient(circle at top right, rgba(18, 181, 122, 0.1), transparent 24%),
    radial-gradient(circle at top left, rgba(123, 138, 162, 0.08), transparent 22%),
    linear-gradient(180deg, #f7fafd 0%, #eef4f8 100%);
  font-family: "Inter", sans-serif;
  font-size: 15px;
}

.dashboard-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
}

.dashboard-hero__heading {
  display: flex;
  align-items: center;
  gap: 18px;
}

.dashboard-hero__logo {
  flex-shrink: 0;
}

.dashboard-title {
  margin: 0;
  color: var(--dashboard-text);
  font-size: 36px;
  font-weight: 800;
  line-height: 1.1;
}

.dashboard-subtitle {
  margin-top: 8px;
  color: var(--dashboard-muted);
  font-size: 14px;
  max-width: 620px;
}

.dashboard-hero__company {
  min-width: 300px;
  padding: 18px 20px;
  border: 1px solid #e1e8f0;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 18px 32px rgba(31, 42, 68, 0.06);
  color: var(--dashboard-text);
}

.dashboard-hero__eyebrow,
.dashboard-section__eyebrow {
  color: var(--dashboard-muted);
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
}

.dashboard-hero__name {
  margin-top: 8px;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.2;
}

.dashboard-hero__chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 14px;
}

.dashboard-hero__summary {
  margin-top: 12px;
  color: var(--dashboard-muted-strong);
  font-size: 13px;
  line-height: 1.55;
}

.dashboard-chip {
  border-radius: 999px !important;
  border: 1px solid transparent !important;
  background: var(--dashboard-secondary-soft) !important;
  color: var(--dashboard-text) !important;
  font-weight: 700;
}

.dashboard-chip--primary,
.dashboard-chip--success {
  background: var(--dashboard-accent-soft) !important;
  border-color: rgba(18, 181, 122, 0.2) !important;
  color: #0f8f61 !important;
}

.dashboard-chip--warning {
  background: var(--dashboard-warning-soft) !important;
  border-color: rgba(199, 146, 74, 0.2) !important;
  color: #a87029 !important;
}

.dashboard-chip--danger {
  background: var(--dashboard-danger-soft) !important;
  border-color: rgba(211, 107, 107, 0.2) !important;
  color: #c85a5a !important;
}

.dashboard-chip--ghost {
  background: rgba(123, 138, 162, 0.08) !important;
  border-color: rgba(123, 138, 162, 0.16) !important;
  color: var(--dashboard-muted-strong) !important;
}

.dashboard-filter-card {
  padding: 20px 22px;
  border-radius: 22px !important;
  border: 1px solid #e1e8f0 !important;
  background: rgba(255, 255, 255, 0.98) !important;
  box-shadow: 0 18px 36px rgba(31, 42, 68, 0.06);
}

.dashboard-filter-card__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
}

.dashboard-filter-card__field {
  min-width: 0;
}

.dashboard-filter-card__field::v-deep .v-input__slot {
  background: #ffffff !important;
}

.dashboard-filter-card__field::v-deep fieldset {
  border-color: #dbe4ee !important;
}

.dashboard-filter-card__field::v-deep .v-label,
.dashboard-filter-card__field::v-deep .v-icon {
  color: #7b8aa2 !important;
}

.dashboard-filter-card__field::v-deep .v-select__selection {
  color: #1f2a44 !important;
}

.dashboard-filter-card__footer {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
  font-size: 12px;
  color: var(--dashboard-muted);
}

.dashboard-driver-launch {
  position: relative;
  overflow: hidden;
  border-radius: 26px !important;
  border: 1px solid rgba(18, 181, 122, 0.16) !important;
  background:
    radial-gradient(circle at top left, rgba(18, 181, 122, 0.14), transparent 30%),
    radial-gradient(circle at bottom right, rgba(123, 138, 162, 0.1), transparent 26%),
    linear-gradient(135deg, #f5fffb 0%, #ffffff 58%, #f7fafc 100%) !important;
  box-shadow: 0 24px 48px rgba(31, 42, 68, 0.08) !important;
  color: var(--dashboard-text);
}

.dashboard-driver-launch::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, rgba(18, 181, 122, 0.04), transparent 26%, rgba(123, 138, 162, 0.05) 60%, transparent 78%);
  pointer-events: none;
}

.dashboard-driver-launch__grid {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(280px, 0.82fr);
  gap: 24px;
  align-items: center;
  padding: 28px;
}

.dashboard-driver-launch__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(18, 181, 122, 0.08);
  border: 1px solid rgba(18, 181, 122, 0.16);
  color: #0f8f61;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.dashboard-driver-launch__pulse {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #64dcc5;
  box-shadow: 0 0 0 0 rgba(100, 220, 197, 0.5);
  animation: dashboardDriverPulse 1.8s infinite;
}

.dashboard-driver-launch__title {
  margin: 18px 0 0;
  font-size: 34px;
  font-weight: 900;
  line-height: 1.05;
  max-width: 700px;
}

.dashboard-driver-launch__text {
  margin: 14px 0 0;
  max-width: 760px;
  color: var(--dashboard-muted-strong);
  font-size: 15px;
  line-height: 1.7;
}

.dashboard-driver-launch__number {
  display: inline-flex;
  align-items: center;
  margin: 0 4px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(18, 181, 122, 0.1);
  border: 1px solid rgba(18, 181, 122, 0.16);
  color: #0f8f61;
  font-weight: 800;
  white-space: nowrap;
}

.dashboard-driver-launch__steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 24px;
}

.dashboard-driver-launch__step {
  padding: 16px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e7edf4;
  box-shadow: 0 12px 24px rgba(31, 42, 68, 0.05);
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  min-height: 120px;
}

.dashboard-driver-launch__step strong {
  display: block;
  font-size: 14px;
  color: var(--dashboard-text);
}

.dashboard-driver-launch__step p {
  margin: 8px 0 0;
  color: var(--dashboard-muted);
  font-size: 12px;
  line-height: 1.55;
}

.dashboard-driver-launch__step-index {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #6ad7c3 0%, #538caf 100%);
  color: #ffffff;
  font-size: 12px;
  font-weight: 900;
}

.dashboard-driver-launch__actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 22px;
}

.dashboard-driver-launch__btn {
  border-radius: 14px !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 700;
}

.dashboard-driver-launch__btn--primary {
  background: linear-gradient(135deg, #18c58b 0%, #12b57a 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 16px 28px rgba(18, 181, 122, 0.18) !important;
}

.dashboard-driver-launch__btn--ghost {
  color: #526277 !important;
}

.dashboard-driver-launch__visual {
  position: relative;
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dashboard-driver-launch__badge {
  position: absolute;
  top: 12px;
  right: 18px;
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(18, 181, 122, 0.14);
  color: var(--dashboard-muted-strong);
  font-size: 12px;
  line-height: 1.4;
  box-shadow: 0 16px 28px rgba(31, 42, 68, 0.08);
}

.dashboard-driver-launch__badge strong {
  display: block;
  margin-top: 4px;
  color: var(--dashboard-text);
  font-size: 18px;
  line-height: 1.1;
}

.dashboard-driver-phone {
  width: 236px;
  border-radius: 32px;
  padding: 12px;
  background: linear-gradient(180deg, #ffffff, #f1f6fa);
  border: 1px solid #dce5ee;
  box-shadow: 0 24px 48px rgba(31, 42, 68, 0.12);
}

.dashboard-driver-phone__notch {
  width: 78px;
  height: 10px;
  border-radius: 999px;
  background: #d5dee8;
  margin: 0 auto 12px;
}

.dashboard-driver-phone__screen {
  border-radius: 24px;
  padding: 16px;
  background:
    radial-gradient(circle at top right, rgba(18, 181, 122, 0.12), transparent 26%),
    linear-gradient(180deg, #f8fbfd, #eef4f8);
  min-height: 300px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dashboard-driver-phone__header {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--dashboard-muted-strong);
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 6px;
}

.dashboard-driver-phone__status {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #64dcc5;
}

.dashboard-driver-phone__bubble {
  max-width: 86%;
  padding: 12px 14px;
  border-radius: 18px;
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.14);
}

.dashboard-driver-phone__bubble--out {
  align-self: flex-end;
  background: linear-gradient(135deg, #18c58b 0%, #12b57a 100%);
  color: #ffffff;
  font-weight: 700;
  border-bottom-right-radius: 6px;
}

.dashboard-driver-phone__bubble--in {
  align-self: flex-start;
  background: #ffffff;
  border: 1px solid #dfe7ef;
  color: #526277;
  border-bottom-left-radius: 6px;
}

.dashboard-driver-phone__footer {
  margin-top: auto;
  display: flex;
  justify-content: center;
  gap: 8px;
  padding-top: 8px;
}

.dashboard-driver-phone__footer span {
  width: 34px;
  height: 4px;
  border-radius: 999px;
  background: #d7e1e9;
}

@keyframes dashboardDriverPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(100, 220, 197, 0.46);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(100, 220, 197, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(100, 220, 197, 0);
  }
}

.dashboard-refresh-btn {
  background: linear-gradient(135deg, #18c58b 0%, #12b57a 100%) !important;
  color: #ffffff !important;
  font-weight: 700;
  border-radius: 14px;
  text-transform: none;
  padding: 0 18px !important;
  box-shadow: 0 14px 28px rgba(18, 181, 122, 0.18) !important;
}

.dashboard-card {
  border-radius: 18px !important;
  box-shadow: 0 16px 32px rgba(31, 42, 68, 0.06);
  margin-bottom: 0;
  font-size: 14px;
  overflow: hidden;
}

.dashboard-surface-card {
  background: var(--dashboard-surface-strong) !important;
  border: 1px solid #e1e8f0 !important;
  color: var(--dashboard-text);
  box-shadow: 0 20px 38px rgba(31, 42, 68, 0.07) !important;
}

.dashboard-kpi-card {
  position: relative;
  border: 1px solid rgba(146, 165, 184, 0.14) !important;
}

.dashboard-kpi-card::before {
  content: '';
  position: absolute;
  inset: 0 0 auto 0;
  height: 3px;
}

.green-gradient {
  background: linear-gradient(180deg, #ffffff 0%, #f1fbf7 100%);
}

.blue-gradient {
  background: linear-gradient(180deg, #ffffff 0%, #f3f8fc 100%);
}

.amber-gradient {
  background: linear-gradient(180deg, #ffffff 0%, #fcf8f1 100%);
}

.slate-gradient {
  background: linear-gradient(180deg, #ffffff 0%, #f4f7fa 100%);
}

.green-gradient::before {
  background: linear-gradient(90deg, rgba(83, 183, 166, 0) 0%, rgba(83, 183, 166, 0.9) 24%, rgba(83, 183, 166, 0.18) 100%);
}

.blue-gradient::before {
  background: linear-gradient(90deg, rgba(140, 165, 182, 0) 0%, rgba(140, 165, 182, 0.8) 24%, rgba(140, 165, 182, 0.18) 100%);
}

.amber-gradient::before {
  background: linear-gradient(90deg, rgba(199, 150, 97, 0) 0%, rgba(199, 150, 97, 0.75) 24%, rgba(199, 150, 97, 0.16) 100%);
}

.slate-gradient::before {
  background: linear-gradient(90deg, rgba(146, 165, 184, 0) 0%, rgba(146, 165, 184, 0.72) 24%, rgba(146, 165, 184, 0.16) 100%);
}

.dashboard-kpi__icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  background: rgba(18, 181, 122, 0.08);
}

.green-gradient .dashboard-kpi__icon {
  color: var(--dashboard-accent);
}

.blue-gradient .dashboard-kpi__icon,
.slate-gradient .dashboard-kpi__icon {
  color: var(--dashboard-secondary);
}

.amber-gradient .dashboard-kpi__icon {
  color: var(--dashboard-warning);
}

.dashboard-kpi__header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.dashboard-kpi__value {
  font-size: 28px;
  font-weight: 800;
  line-height: 1.15;
  margin-bottom: 8px;
}

.dashboard-kpi__caption {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--dashboard-muted);
}

.dashboard-kpi__chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 8px 0 2px;
}

.dashboard-section__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.dashboard-section__title {
  font-size: 22px;
  font-weight: 800;
  line-height: 1.15;
  margin-top: 6px;
}

.dashboard-section__subtitle {
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--dashboard-muted);
}

.dashboard-metric-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.dashboard-metric-strip__item {
  padding: 12px 14px;
  border-radius: 14px;
  background: var(--dashboard-surface-soft);
  border: 1px solid var(--dashboard-border);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dashboard-metric-strip__item span,
.dashboard-mini-grid__item span,
.dashboard-ranking__meta span,
.dashboard-aside__item span,
.dashboard-subsection__item span,
.dashboard-trip-list__detail,
.dashboard-trip-list__meta,
.dashboard-empty-state {
  color: var(--dashboard-muted);
  font-size: 12px;
  line-height: 1.5;
}

.dashboard-metric-strip__item strong,
.dashboard-mini-grid__item strong,
.dashboard-aside__value,
.dashboard-ranking__value,
.dashboard-subsection__value,
.dashboard-trip-list__aside strong {
  font-size: 16px;
  font-weight: 800;
  color: var(--dashboard-text);
}

.dashboard-bar-chart {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(28px, 1fr));
  gap: 10px;
  align-items: end;
  margin-top: 18px;
}

.dashboard-bar-chart__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.dashboard-bar-chart__columns {
  height: 120px;
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.dashboard-bar-chart__bar {
  width: 10px;
  border-radius: 999px 999px 0 0;
  min-height: 4px;
  transition: height 0.2s ease;
}

.dashboard-bar-chart__bar--in {
  background: linear-gradient(180deg, #73c9bb 0%, #53b7a6 100%);
  box-shadow: 0 12px 18px rgba(83, 183, 166, 0.18);
}

.dashboard-bar-chart__bar--out {
  background: linear-gradient(180deg, #a8bbc8 0%, #7f97a9 100%);
  box-shadow: 0 12px 18px rgba(127, 151, 169, 0.14);
}

.dashboard-bar-chart__label {
  font-size: 11px;
  color: var(--dashboard-muted);
  text-align: center;
}

.dashboard-donut-wrap {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
}

.dashboard-donut {
  width: 164px;
  height: 164px;
  border-radius: 50%;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 0 1px rgba(123, 138, 162, 0.08);
}

.dashboard-donut__center {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--dashboard-text);
}

.dashboard-donut__center strong {
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
}

.dashboard-donut__center span {
  margin-top: 6px;
  font-size: 12px;
  color: var(--dashboard-muted);
}

.dashboard-legend {
  display: grid;
  gap: 10px;
  min-width: 180px;
  flex: 1;
}

.dashboard-legend__item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 10px;
  align-items: center;
  font-size: 13px;
  color: var(--dashboard-muted-strong);
}

.dashboard-legend__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.dashboard-legend__dot.is-green {
  background: var(--dashboard-accent);
}

.dashboard-legend__dot.is-amber {
  background: var(--dashboard-warning);
}

.dashboard-legend__dot.is-red {
  background: var(--dashboard-danger);
}

.dashboard-legend__dot.is-slate {
  background: #8195a4;
}

.dashboard-mini-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 22px;
}

.dashboard-mini-grid__item {
  padding: 12px 14px;
  border-radius: 14px;
  background: var(--dashboard-surface-soft);
  border: 1px solid var(--dashboard-border);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dashboard-progress-list {
  display: grid;
  gap: 12px;
  margin-top: 14px;
}

.dashboard-progress-list__row {
  display: grid;
  grid-template-columns: 120px 1fr auto;
  gap: 12px;
  align-items: center;
  color: var(--dashboard-muted-strong);
  font-size: 13px;
}

.dashboard-progress-list__track,
.dashboard-ranking__track {
  height: 10px;
  border-radius: 999px;
  background: rgba(146, 165, 184, 0.12);
  overflow: hidden;
}

.dashboard-progress-list__fill,
.dashboard-ranking__fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(135deg, #76c5b7 0%, #6f93aa 100%);
}

.dashboard-ranking {
  display: grid;
  gap: 14px;
}

.dashboard-ranking__title,
.dashboard-aside__title,
.dashboard-subsection__title {
  margin-bottom: 14px;
  font-size: 15px;
  font-weight: 800;
  color: var(--dashboard-text);
}

.dashboard-ranking__item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 140px auto;
  gap: 12px;
  align-items: center;
}

.dashboard-ranking__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dashboard-aside__list,
.dashboard-subsection__list,
.dashboard-trip-list,
.dashboard-alert-list {
  display: grid;
  gap: 12px;
}

.dashboard-aside__item,
.dashboard-subsection__item {
  padding: 12px 14px;
  border-radius: 14px;
  background: var(--dashboard-surface-soft);
  border: 1px solid var(--dashboard-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.dashboard-aside__item > div:first-child,
.dashboard-subsection__item > div:first-child {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dashboard-alert-list__item {
  display: flex;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  background: var(--dashboard-surface-soft);
  border: 1px solid var(--dashboard-border);
}

.dashboard-alert-list__icon {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dashboard-alert-list__icon.is-high {
  background: var(--dashboard-danger-soft);
  color: var(--dashboard-danger);
}

.dashboard-alert-list__icon.is-medium {
  background: var(--dashboard-warning-soft);
  color: var(--dashboard-warning);
}

.dashboard-alert-list__content {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dashboard-alert-list__title,
.dashboard-trip-list__title {
  font-size: 14px;
  font-weight: 800;
  color: var(--dashboard-text);
}

.dashboard-alert-list__description {
  font-size: 13px;
  line-height: 1.45;
  color: var(--dashboard-muted-strong);
}

.dashboard-alert-list__meta {
  font-size: 12px;
  color: var(--dashboard-muted);
  line-height: 1.45;
}

.dashboard-subsection {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid rgba(146, 165, 184, 0.14);
}

.dashboard-empty-state {
  padding: 14px 0;
  text-align: left;
}

.dashboard-empty-state--compact {
  padding-top: 4px;
}

.dashboard-trip-list__item {
  padding: 14px 16px;
  border-radius: 16px;
  background: var(--dashboard-surface-soft);
  border: 1px solid var(--dashboard-border);
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.dashboard-trip-list__meta,
.dashboard-trip-list__detail {
  margin-top: 4px;
  max-width: 480px;
}

.dashboard-trip-list__aside {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  text-align: right;
}

.dashboard-status-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.dashboard-status-pill.is-active {
  background: var(--dashboard-accent-soft);
  color: var(--dashboard-accent);
}

.dashboard-status-pill.is-done {
  background: var(--dashboard-secondary-soft);
  color: #5e788d;
}

.dashboard-status-pill.is-cancelled {
  background: var(--dashboard-danger-soft);
  color: #c85a5a;
}

.dashboard-status-pill.is-pending {
  background: var(--dashboard-warning-soft);
  color: #b47f37;
}

@media (max-width: 1264px) {
  .dashboard-filter-card__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .dashboard-driver-launch__grid {
    grid-template-columns: 1fr;
  }

  .dashboard-driver-launch__visual {
    min-height: 240px;
  }
}

@media (max-width: 960px) {
  .dashboard-bg {
    padding-top: 24px !important;
  }

  .dashboard-title {
    font-size: 30px;
  }

  .dashboard-filter-card__grid,
  .dashboard-metric-strip,
  .dashboard-mini-grid,
  .dashboard-driver-launch__steps {
    grid-template-columns: 1fr;
  }

  .dashboard-driver-launch__grid {
    padding: 20px;
  }

  .dashboard-driver-launch__title {
    font-size: 28px;
  }

  .dashboard-driver-launch__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .dashboard-driver-launch__visual {
    min-height: auto;
    padding-top: 12px;
  }

  .dashboard-driver-launch__badge {
    position: static;
    margin-bottom: 16px;
  }

  .dashboard-donut-wrap,
  .dashboard-trip-list__item {
    flex-direction: column;
  }

  .dashboard-trip-list__aside {
    align-items: flex-start;
    text-align: left;
  }

  .dashboard-progress-list__row,
  .dashboard-ranking__item {
    grid-template-columns: 1fr;
  }

  .dashboard-aside__item,
  .dashboard-subsection__item {
    align-items: flex-start;
    flex-direction: column;
  }

  .dashboard-filter-card__footer {
    flex-direction: column;
  }
  }
</style>
